import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Segmented, Skeleton, Typography } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { projectPath } from "../../config/nav";
import { legacyProjectSites, siteRows, type SiteDTO } from "../sites/siteListModel";
import "../sites/SiteCenter.css";

const { Title, Text } = Typography;
type Filter = "all" | "attention" | "healthy";

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const { projects, API_BASE_URL } = useGlobal();
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState<SiteDTO[]>([]);
  const [sitesAvailable, setSitesAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/sites`);
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "sites unavailable");
      setSites(response.data?.data?.list || []);
      setSitesAvailable(true);
    } catch {
      setSitesAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => { void loadSites(); }, [loadSites]);
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    navigate("/sites/new/setup", { replace: true });
  }, [navigate, searchParams]);

  const rows = useMemo(
    () => siteRows(sitesAvailable ? sites : legacyProjectSites(projects), projects),
    [projects, sites, sitesAvailable]
  );
  const visibleRows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return rows.filter((row) => {
      if (filter === "attention" && !row.needsAttention) return false;
      if (filter === "healthy" && row.needsAttention) return false;
      return !normalized || `${row.name} ${row.domain}`.toLocaleLowerCase().includes(normalized);
    });
  }, [filter, query, rows]);

  const openRecommendation = (row: (typeof rows)[number]) => {
    if (row.id.startsWith("legacy-") && row.projectKey) {
      navigate(projectPath(row.projectKey, row.recommendationSubPath));
      return;
    }
    const base = `/sites/${encodeURIComponent(row.id)}`;
    if (row.recommendation === "继续接入") navigate(`${base}/setup`);
    else if (row.recommendationSubPath === "settings") navigate(`${base}/settings`);
    else navigate(base);
  };

  return (
    <main className="site-center" aria-labelledby="site-center-title">
      <header className="site-center__heading">
        <div>
          <Title id="site-center-title" level={2}>项目</Title>
          <Text type="secondary">统一管理项目接入、知识、SDK 与 AI 能力状态</Text>
        </div>
        <Button type="primary" size="large" icon={<i className="ri-add-line" />} onClick={() => navigate("/sites/new/setup")}>新建项目</Button>
      </header>

      <div className="site-center__toolbar">
        <Input
          allowClear
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          prefix={<i className="ri-search-line" />}
          placeholder="搜索项目名称或域名"
          aria-label="搜索项目名称或域名"
        />
        <Segmented<Filter>
          value={filter}
          onChange={setFilter}
          options={[{ label: "全部", value: "all" }, { label: "需处理", value: "attention" }, { label: "正常", value: "healthy" }]}
        />
      </div>

      <section className="site-list" aria-label="项目列表">
        <div className="site-list__header" aria-hidden="true">
          <span>项目</span><span>环境</span><span>运行状态</span><span>知识数</span><span>动作健康</span><span>最近活动</span><span>推荐操作</span>
        </div>
        {loading ? <Skeleton active paragraph={{ rows: 4 }} className="site-list__loading" /> : visibleRows.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={rows.length ? "没有匹配的项目" : "还没有项目"}>
            {rows.length === 0 ? <Button type="primary" onClick={() => navigate("/sites/new/setup")}>新建第一个项目</Button> : null}
          </Empty>
        ) : visibleRows.map((row) => (
          <article className="site-row" key={row.id}>
            <div className="site-row__identity">
              <span className="site-row__icon"><i className="ri-global-line" /></span>
              <span><strong>{row.name}</strong><small>{row.domain}</small></span>
            </div>
            <div data-label="环境">{row.environment}</div>
            <div data-label="运行状态" className={`site-tone site-tone--${row.runtimeTone}`}><i className="ri-checkbox-blank-circle-fill" />{row.runtimeLabel}</div>
            <div data-label="知识数">{row.knowledgeCount.toLocaleString()}</div>
            <div data-label="动作健康" className={`site-health site-health--${row.healthTone}`}><strong>{row.healthLabel}</strong><small>{row.healthDetail}</small></div>
            <div data-label="最近活动">{row.lastActivity}</div>
            <div data-label="推荐操作"><Button onClick={() => openRecommendation(row)}>{row.recommendation}<i className="ri-arrow-right-s-line" /></Button></div>
          </article>
        ))}
      </section>

      {!sitesAvailable ? <p className="site-center__compat"><i className="ri-information-line" /> 当前由旧项目兼容数据提供列表，原有 Key 与核心能力不受影响。</p> : null}

    </main>
  );
};

export default ProjectList;
